import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { QuoteEditorPreviewProps } from 'modules/Cube/view/layouts/quote/QuoteEditorPreview/QuoteEditorPreview.types'
import {
  useQuoteDocxDownload,
  useQuotePdfDownload
} from '@sequencehq/quote-content'
import { ViewMode } from '../../QuoteEditorHeader/useQuoteEditorHeader'
import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { usePermissions } from 'lib/rbac'
import { useLogEvent } from 'lib/postHog/useLogEvent'

type UseHeader = (hook: QuoteEditorPreviewProps) => {
  exitBackButton: {
    onClick: () => void
  }
  downloadPdfButton: {
    onClick: () => void
  }
  downloadDocxButton: {
    onClick: () => void
  }
  viewMode: {
    current: ViewMode
    onChange: (mode: string) => void
    options: Array<{
      value: ViewMode
      label: string
      disabled?: boolean
      'data-testid'?: string
    }>
  }
  cubeContext: ReturnType<typeof useCubeContext>
}

export const useHeader: UseHeader = hook => {
  const navigate = useNavigate()
  const cubeContext = useCubeContext()
  const { can } = usePermissions()
  const { logEvent } = useLogEvent()

  const fetchPdfQuoteRendererData = useCallback(
    () => hook.pdfRendererData,
    [hook.pdfRendererData]
  )

  const assetLoader = useCallback(async ({ assetId }: { assetId: string }) => {
    const res = await dashboard20240730Client.getAsset({
      id: assetId
    })

    if (!res.data || res.error) {
      return undefined
    }

    return { urlSigned: res.data.url }
  }, [])

  const pdfQuoteDownloadHook = useQuotePdfDownload({
    fetchPdfQuoteRendererData,
    assetLoader
  })

  const docxQuoteDownloadHook = useQuoteDocxDownload({
    fetchQuoteData: fetchPdfQuoteRendererData,
    assetLoader
  })

  const isATemplate = cubeContext.queries.quote.isATemplate

  const onSelectEdit = useCallback(() => {
    if (isATemplate) {
      navigate(`/quote-templates/${hook.quoteId}`)
      return
    }

    navigate(`/quotes/${hook.quoteId}`)
  }, [hook.quoteId, isATemplate, navigate])

  const canEdit = isATemplate
    ? can('org.quote:template.update')
    : can('org.quote:update')

  return {
    exitBackButton: {
      onClick: onSelectEdit
    },
    downloadPdfButton: {
      onClick: () => {
        void pdfQuoteDownloadHook.onClick()
      }
    },
    downloadDocxButton: {
      onClick: () => {
        logEvent({
          eventName: 'quote_download_docx',
          metadata: { quoteId: hook.quoteId }
        })
        docxQuoteDownloadHook.onClick().catch(() => {
          logEvent({
            eventName: 'quote_download_docx_failed',
            metadata: { quoteId: hook.quoteId }
          })
        })
      }
    },
    viewMode: {
      current: ViewMode.Preview,
      onChange: (mode: string) => {
        if (mode === ViewMode.Edit) {
          onSelectEdit()
        }
      },
      options: [
        {
          value: ViewMode.Edit,
          label: 'Edit',
          'data-testid': 'preview.quoteEditor.viewMode.edit',
          disabled: !canEdit
        },
        {
          value: ViewMode.Preview,
          label: 'Preview',
          'data-testid': 'preview.quoteEditor.viewMode.preview',
          disabled:
            !cubeContext.queries.availableFeatures.quote.preview.available
              .enabled
        }
      ]
    },
    cubeContext
  }
}
